import React, { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { IoChevronDown, IoClose, IoSettingsSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { HiMagnifyingGlass } from "react-icons/hi2";

import Comments from "./Comments";
import DebounceInput from "../../../../../components/DebounceInput";
import MultiSelect from "../../../../../components/MultiSelect";
import SelectUser from "../../../../../components/selectUser";
import api from "../../../../../services/api";

import Gerard from "./gerard";
import SingleSelect from "../../../../../components/SingleSelect";

export default ({ epic, setEpic, project, setProject }) => {
  const [budgets, setBudgets] = useState([]);

  useEffect(() => {
    async function fetchBudgets() {
      const { data } = await api.post("/budget/search", {
        projectId: epic.project_id,
        status: "active",
      });
      setBudgets(data);
    }
    if (epic.project_id) fetchBudgets();
  }, [epic.project_id]);

  return (
    <div className="flex gap-2">
      <div className="w-3/12 bg-white p-3 relative">
        <div className="absolute top-2 right-2">
          <ConfigSelector
            project={project}
            onConfigChange={async (newConfig) => {
              const { ok, data } = await api.put(`/project/${project._id}`, { epic_config: newConfig });
              console.log(ok, data);
              if (!ok) return toast.error("Failed to update config");
              setProject({ ...project, epic_config: data.epic_config });
            }}
          />
        </div>
        <div className="flex flex-col">
          {/* Business Objective */}
          {project.epic_config.businessObjective && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">Business Objective</label>
              <DebounceInput
                debounceTimeout={600}
                className="input w-full text-xs py-2 px-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1.5 focus:ring-blue-500"
                isTextArea={true}
                placeholder="Describe the business objective..."
                type="text"
                id="business_objective"
                value={epic.business_objective || ""}
                onChange={async (e) => {
                  const { data } = await api.put(`/epic/${epic._id}`, { business_objective: e.target.value });
                  setEpic(data);
                  toast.success("Business Objective updated!");
                }}
              />
            </div>
          )}

          {/* Module */}
          {project.epic_config.module && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">Module</label>
              <SelectModule
                value={epic.module}
                onChange={async (e) => {
                  const { data } = await api.put(`/epic/${epic._id}`, { module: e });
                  setEpic(data);
                  toast.success("Module updated!");
                }}
                projectId={epic.project_id}
              />
            </div>
          )}

          {project.epic_config.tags && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">Tags</label>
              <SelectTags
                value={epic.tags}
                onChange={async (e) => {
                  const { data } = await api.put(`/epic/${epic._id}`, { tags: e });
                  setEpic(data);
                  toast.success("Tags updated!");
                }}
                projectId={epic.project_id}
              />
            </div>
          )}

          {project.epic_config.owner && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">Owner</label>
              <SelectUser
                value={{ _id: epic.user_id, name: epic.user_name, avatar: epic.user_avatar }}
                onChange={async (e) => {
                  const { data } = await api.put(`/epic/${epic._id}`, {
                    user_id: e._id,
                    user_name: e.name,
                    user_avatar: e.avatar,
                  });
                  setEpic(data);
                  toast.success("User updated!");
                }}
                buttonClassName="border-gray-300 p-1"
                avatarClassName="h-8 w-8"
              />
            </div>
          )}
        </div>

        {/* Status & Priority Section */}
        {(project.epic_config.status || project.epic_config.urgent || project.epic_config.priority || project.epic_config.charge || project.epic_config.business_contribution) && (
          <div className="rounded-md bg-gray-50 p-2 mt-2 border border-gray-100">
            <div className="grid grid-cols-2 gap-2">
              {/* Status */}
              {project.epic_config.status && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Status</label>
                  <Select
                    value={epic?.status}
                    options={[
                      { value: "TODO", label: "TODO" },
                      { value: "UXUI", label: "UXUI" },
                      { value: "CONTENT_WRITING", label: "CONTENT WRITING" },
                      { value: "READY_TO_DEV", label: "READY TO DEV" },
                      { value: "IN_PROGRESS", label: "IN PROGRESS" },
                      { value: "NEED_INFO", label: "NEED INFO" },
                      { value: "READY_FOR_QA", label: "READY FOR QA" },
                      { value: "READY_TO_RELEASE", label: "READY TO RELEASE" },
                      { value: "DONE", label: "DONE" },
                    ]}
                    placeholder="Select status"
                    onChange={async (e) => {
                      const { data } = await api.put(`/epic/${epic._id}`, { status: e.target.value });
                      setEpic(data);
                      toast.success("Status updated!");
                    }}
                  />
                </div>
              )}

              {/* Urgent */}
              {project.epic_config.urgent && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Urgent</label>
                  <Select
                    value={epic.urgent ? "yes" : "no"}
                    options={[
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                    ]}
                    placeholder="Select urgent status"
                    onChange={async (e) => {
                      const urgentValue = e.target.value === "yes" ? true : false;
                      const { data } = await api.put(`/epic/${epic._id}`, { urgent: urgentValue });
                      setEpic(data);
                      toast.success("Urgent updated!");
                    }}
                  />
                </div>
              )}

              {/* Priority */}
              {project.epic_config.priority && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Priority</label>
                  <Select
                    value={epic.priority}
                    options={[
                      { value: "NOW", label: "NOW" },
                      { value: "NEXT", label: "NEXT" },
                      { value: "LATER", label: "LATER" },
                    ]}
                    placeholder="Select priority"
                    onChange={async (e) => {
                      const { data } = await api.put(`/epic/${epic._id}`, { priority: e.target.value });
                      setEpic(data);
                      toast.success("Priority updated!");
                    }}
                  />
                </div>
              )}
              {/* Charge */}
              {project.epic_config.charge && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-gray-700">Charge</label>
                  <Select
                    value={epic.charge}
                    options={[
                      { value: "XS", label: "XS" },
                      { value: "S", label: "S" },
                      { value: "M", label: "M" },
                      { value: "L", label: "L" },
                      { value: "XL", label: "XL" },
                    ]}
                    placeholder="Select charge"
                    onChange={async (e) => {
                      const { data } = await api.put(`/epic/${epic._id}`, { charge: e.target.value });
                      setEpic(data);
                      toast.success("Charge updated!");
                    }}
                  />
                </div>
              )}

              {/* Business Contribution */}
              {project.epic_config.business_contribution && (
                <div className="space-y-1 col-span-2">
                  <label className="text-xs font-semibold text-gray-700">Business Contribution</label>
                  <Select
                    value={epic.business_contribution}
                    options={[
                      { value: "LOW", label: "LOW" },
                      { value: "MEDIUM", label: "MEDIUM" },
                      { value: "HIGH", label: "HIGH" },
                    ]}
                    placeholder="Select contribution"
                    onChange={async (e) => {
                      const { data } = await api.put(`/epic/${epic._id}`, { business_contribution: e.target.value });
                      setEpic(data);
                      toast.success("Business Contribution updated!");
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {(project.epic_config.resourceProfile ||
          project.epic_config.daysRequired ||
          project.epic_config.dailyRate ||
          project.epic_config.estimation ||
          project.epic_config.budget) && (
          <div className="rounded-md bg-gray-50 p-2 mt-2 border border-gray-100">
            {/* Resource Profile */}
            {project.epic_config.resourceProfile && (
              <div className="space-y-1 mb-2">
                <label className="text-xs font-semibold text-gray-700">Resource Profile</label>
                <Select
                  value={epic.resource_profile}
                  options={[
                    { value: "DEVELOPER", label: "Developer" },
                    { value: "EXPERIMENTED DEVELOPER", label: "Experimented Developer" },
                    { value: "IA ENGINEER", label: "IA Engineer" },
                    { value: "CTO", label: "CTO" },
                    { value: "PRODUCT", label: "Product" },
                    { value: "DevOps", label: "DevOps" },
                  ]}
                  placeholder="Select resource profile"
                  onChange={async (e) => {
                    let tjm = 500;
                    if (e.target.value === "CTO") tjm = 650;
                    else if (e.target.value === "EXPERIMENTED DEVELOPER") tjm = 500;
                    else if (e.target.value === "DEVELOPER") tjm = 350;
                    else if (e.target.value === "PRODUCT") tjm = 500;
                    else if (e.target.value === "IA ENGINEER") tjm = 600;
                    else if (e.target.value === "DevOps") tjm = 750;
                    const estimation = epic.days_required * tjm;
                    const { data } = await api.put(`/epic/${epic._id}`, {
                      resource_profile: e.target.value,
                      tjm,
                      estimation,
                    });
                    setEpic(data);
                    toast.success("Resource profile updated!");
                  }}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {/* Days Required */}
              {project.epic_config.daysRequired && (
                <div className="space-y-1 mb-2">
                  <label className="text-xs font-semibold text-gray-700">Days Required</label>
                  <EstimationDays
                    value={epic.days_required}
                    onChange={async (e) => {
                      const tjm = epic.tjm || 500;
                      const estimation = e * tjm;
                      const { data } = await api.put(`/epic/${epic._id}`, {
                        days_required: e,
                        estimation,
                      });
                      setEpic(data);
                      toast.success("Estimation updated!");
                    }}
                  />
                </div>
              )}

              {/* Daily Rate */}
              {project.epic_config.dailyRate && (
                <div className="space-y-1 mb-2">
                  <label className="text-xs font-semibold text-gray-700">Daily Rate (€)</label>
                  <input
                    type="number"
                    className="input text-xs py-1.5 px-2 w-full"
                    value={epic.tjm}
                    onWheel={(e) => e.target.blur()}
                    onChange={async (e) => {
                      if (e.target.value === epic.tjm) return;
                      const estimation = epic.days_required * e.target.value;
                      const { data } = await api.put(`/epic/${epic._id}`, {
                        tjm: e.target.value,
                        estimation,
                      });
                      setEpic(data);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Total Estimation */}
            {project.epic_config.estimation && (
              <div className="space-y-1 mb-2">
                <label className="text-xs font-semibold text-gray-700">Estimation (€)</label>
                <EstimationPrice
                  value={epic.estimation}
                  onChange={async (e) => {
                    const { data } = await api.put(`/epic/${epic._id}`, { estimation: e });
                    setEpic(data);
                    toast.success("Estimation updated!");
                  }}
                />
              </div>
            )}

            {/* Budget */}
            {project.epic_config.budget && (
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-700">Budget</label>
                <Select
                  value={epic.budget_id || ""}
                  options={[{ value: "", label: "Select a budget" }, ...budgets.map((budget) => ({ value: budget._id, label: budget.name }))]}
                  placeholder="Select a budget"
                  onChange={async (e) => {
                    const { data } = await api.put(`/epic/${epic._id}`, { budget_id: e.target.value });
                    setEpic(data);
                    toast.success(`Epic linked to ${budgets.find((budget) => budget._id === e.target.value)?.name}!`);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Middle section (Comments) */}
      <div className="w-7/12 bg-white">
        <div className="p-4">
          <Comments epic={epic} setEpic={setEpic} />
        </div>
        <span className="flex items-center text-xs pl-4 pb-1 text-gray-600 italic">
          Created by&nbsp;{epic.user_name} on {moment(epic.created_at).format("DD/MM/YYYY")}
        </span>
      </div>

      {/* Right section (Backlog) */}
      <div className="w-4/12 bg-white p-4">
        <Backlog epic={epic} />
        <div className="mt-4"></div>
        <Gerard epic={epic} setEpic={setEpic} />
      </div>
    </div>
  );
};

const EstimationPrice = ({ value, onChange }) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    setV(value);
  }, [value]);

  return (
    <input
      className="input text-xs py-1.5 px-2 w-full"
      disabled
      type="number"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onWheel={(e) => e.target.blur()}
      onBlur={() => {
        if (v === value) return;
        onChange(v);
      }}
    />
  );
};

const EstimationDays = ({ value, onChange }) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    setV(value);
  }, [value]);

  return (
    <input
      className="input text-xs py-1.5 px-2 w-full"
      type="number"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onWheel={(e) => e.target.blur()}
      onBlur={() => {
        if (v === value) return;
        onChange(v);
      }}
    />
  );
};

const SelectTags = ({ value, onChange, projectId }) => {
  const [tags, setTags] = useState(value || []);
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const tagsId = "tags-select";
  const placeholder = "Add tags";

  // Récupération des options de tags depuis l'API
  useEffect(() => {
    get();
  }, [value]);

  async function get() {
    const { data } = await api.post("/epic/aggregate", { project_id: projectId, key: "tags" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
  }

  // Focus sur l'input de recherche quand le dropdown s'ouvre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Réinitialiser la recherche quand on ferme le dropdown
  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  // Gérer les clics en dehors du dropdown pour le fermer
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(tagsId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Ajouter un tag
  function handleTagSelect(option) {
    if (!tags.includes(option.value)) {
      const newTags = [...tags, option.value];
      setTags(newTags);
      onChange(newTags);
      setSearch("");
    }
  }

  // Créer un nouveau tag
  function handleCreateTag() {
    if (search && !tags.includes(search)) {
      const newTags = [...tags, search];
      setTags(newTags);
      onChange(newTags);
      setSearch("");
      get(); // Refresh options
    }
  }

  // Supprimer un tag
  function removeTag(tagToRemove) {
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(newTags);
    onChange(newTags);
  }

  // Filtrer les options selon la recherche et exclure celles déjà sélectionnées
  const filteredOptions = options.filter((o) => {
    if (tags.includes(o.value)) return false;
    if (!search) return true;
    return o.label.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="relative w-full">
      <div
        id={tagsId}
        onClick={() => setIsOpen(true)}
        className={`min-h-[32px] w-full px-2 py-1 border border-gray-300 rounded-md bg-white flex flex-wrap items-center gap-1 cursor-text ${
          tags.length === 0 ? "justify-between" : ""
        }`}>
        {tags.length === 0 && <span className="text-xs text-gray-500 py-0.5">{placeholder}</span>}

        {tags.map((tag) => (
          <div key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-700 border border-gray-200">
            <span>{tag}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              className="hover:bg-gray-200 rounded-full p-0.5">
              <IoClose className="w-2.5 h-2.5 text-gray-500" />
            </button>
          </div>
        ))}

        {tags.length > 0 && (
          <div className="ml-auto">
            <IoChevronDown className="w-3.5 h-3.5 text-gray-500" />
          </div>
        )}

        {tags.length === 0 && <IoChevronDown className="w-3.5 h-3.5 text-gray-500" />}
      </div>

      {isOpen && (
        <div ref={dropdownRef} className="absolute z-20 mt-1 w-full max-h-52 bg-white shadow-lg rounded-md overflow-auto border border-gray-200">
          {/* Barre de recherche */}
          <div className="sticky top-0 bg-white p-1.5 border-b border-gray-200">
            <div className="relative w-full">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                <HiMagnifyingGlass className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                ref={searchInputRef}
                className="block w-full rounded-md border-gray-300 pl-8 pr-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search or create new tag"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && search) {
                    handleCreateTag();
                  }
                }}
              />
            </div>
          </div>

          {/* Liste des options */}
          <ul className="max-h-40 overflow-y-auto py-1">
            {filteredOptions.map((option) => (
              <li key={option.value} onClick={() => handleTagSelect(option)} className="flex items-center px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100">
                <span className="flex-1">
                  {option.label}
                  {option.count && <span className="ml-1.5 text-xs text-gray-400">({option.count})</span>}
                </span>
              </li>
            ))}

            {/* Option de création */}
            {search && !options.find((o) => o.label.toLowerCase() === search.toLowerCase()) && (
              <li onClick={handleCreateTag} className="flex items-center gap-1.5 px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100">
                <IoClose className="rotate-45 text-gray-500 w-3.5 h-3.5" />
                <span className="flex-1 font-medium text-blue-600">Create tag "{search}"</span>
              </li>
            )}

            {filteredOptions.length === 0 && !search && <li className="px-2 py-1.5 text-xs text-gray-500 italic">No tags available</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const Backlog = ({ epic }) => {
  const [tickets, setTickets] = useState([]);
  const navigate = useNavigate();

  async function fetch() {
    const { data } = await api.post("/notion/search", { epic_id: epic._id, project_id: epic.project_id });
    setTickets(data);
  }

  useEffect(() => {
    fetch();
  }, [epic]);

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Backlog</h2>
      {tickets.map((ticket) => (
        <button
          onClick={() => navigate(`/project/${epic.project_id}/backlog?item_modal_id=${ticket._id}`)}
          key={ticket._id}
          className="flex flex-col bg-white px-6 py-3 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex justify-between w-full">
            <span className="text-sm font-medium text-gray-900">{ticket.name}</span>
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{ticket.status}</span>
          </div>
          {ticket.user_name && (
            <div className="flex gap-2 ">
              {ticket.user_avatar && <img src={ticket.user_avatar} alt={ticket.user_name} className="w-6 h-6 rounded-full border border-gray-200" />}
              <span className="text-sm text-gray-600">{ticket.user_name}</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

const SelectModule = ({ value, onChange, projectId }) => {
  const [module, setModule] = useState(value);
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const moduleId = "module-select";
  const placeholder = "Select a module";

  // Récupération des options de module depuis l'API
  useEffect(() => {
    get();
  }, [value]);

  async function get() {
    const { data } = await api.post("/epic/aggregate", { project_id: projectId, key: "module" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
  }

  // Focus sur l'input de recherche quand le dropdown s'ouvre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Réinitialiser la recherche quand on ferme le dropdown
  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  // Gérer les clics en dehors du dropdown pour le fermer
  useEffect(() => {
    const handleClickOutside = (event) => {
      const idRegex = new RegExp(moduleId);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !idRegex.test(event.target.id)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Sélectionner une option
  function handleOptionClick(option) {
    setModule(option.value);
    onChange(option.value);
    setIsOpen(false);
  }

  // Créer une nouvelle option
  function handleCreateNew() {
    const newOption = { value: search, label: search };
    setModule(newOption.value);
    onChange(newOption.value);
    setIsOpen(false);
    setSearch("");
    get(); // Refresh options après avoir créé
  }

  // Supprimer la sélection
  function handleClear(e) {
    e.stopPropagation(); // Empêcher l'ouverture du dropdown
    setModule(null);
    onChange(null);
  }

  // Filtrer les options selon la recherche
  const filteredOptions = options.filter((o) => {
    if (!search) return true;
    return o.label.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="relative w-full bg-white">
      <button
        id={moduleId}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full inline-flex justify-between items-center px-2 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1.5 focus:ring-blue-500">
        <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap">{module || placeholder}</span>
        <div className="flex items-center gap-1">
          {module && (
            <button onClick={handleClear} className="p-0.5 hover:bg-gray-100 rounded-full">
              <IoClose className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
          <IoChevronDown className="w-3.5 h-3.5 text-gray-500" />
        </div>
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute z-20 mt-1 w-full max-h-52 bg-white shadow-lg rounded-md overflow-auto border border-gray-200">
          {/* Barre de recherche */}
          <div className="sticky top-0 bg-white p-1.5 border-b border-gray-200">
            <div className="relative w-full">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                <HiMagnifyingGlass className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                ref={searchInputRef}
                className="block w-full rounded-md border-gray-300 pl-8 pr-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search or create new"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Liste des options */}
          <ul className="max-h-40 overflow-y-auto py-1">
            {filteredOptions.map((option) => {
              const isSelected = module === option.value;
              return (
                <li
                  key={option.value}
                  onClick={() => handleOptionClick(option)}
                  className={`flex items-center px-2 py-1.5 text-xs cursor-pointer ${isSelected ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"}`}>
                  <span className={`flex-1 ${isSelected ? "font-semibold" : "font-normal"}`}>
                    {option.label}
                    {option.count && <span className="ml-1.5 text-xs text-gray-400">({option.count})</span>}
                  </span>
                </li>
              );
            })}

            {/* Option de création */}
            {search && !filteredOptions.find((o) => o.label.toLowerCase() === search.toLowerCase()) && (
              <li onClick={handleCreateNew} className="flex items-center gap-1.5 px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100">
                <IoClose className="rotate-45 text-gray-500 w-3.5 h-3.5" />
                <span className="flex-1 font-medium text-blue-600">Create "{search}"</span>
              </li>
            )}

            {filteredOptions.length === 0 && !search && <li className="px-2 py-1.5 text-xs text-gray-500 italic">No modules found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const Select = ({ value, options, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(options.find((opt) => opt.value === value) || null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedOption(options.find((opt) => opt.value === value) || null);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange({ target: { value: option.value } });
    setIsOpen(false);
  };

  return (
    <div className="relative w-full bg-white" ref={dropdownRef}>
      <button
        type="button"
        className={`flex items-center justify-between w-full px-2 py-1.5 text-left text-xs font-medium rounded-md border border-gray-200 focus:outline-none focus:ring-1.5 focus:ring-blue-500`}
        onClick={() => setIsOpen(!isOpen)}>
        {selectedOption ? selectedOption.label : placeholder}
        <IoChevronDown className="w-3.5 h-3.5" />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-40 overflow-auto border border-gray-200">
          {options.map((option) => (
            <div
              key={option.value}
              className={`px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100 ${selectedOption?.value === option.value ? "bg-gray-50" : ""}`}
              onClick={() => handleSelect(option)}>
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ConfigSelector = ({ project, onConfigChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState(project.epic_config || {});
  const dropdownRef = useRef(null);

  // Config options with nice labels
  const configOptions = [
    { id: "businessObjective", label: "Business Objective" },
    { id: "module", label: "Module" },
    { id: "tags", label: "Tags" },
    { id: "owner", label: "Owner" },
    { id: "status", label: "Status" },
    { id: "urgent", label: "Urgent Flag" },
    { id: "priority", label: "Priority" },
    { id: "charge", label: "Charge" },
    { id: "business_contribution", label: "Business Contribution" },
    { id: "resourceProfile", label: "Resource Profile" },
    { id: "daysRequired", label: "Days Required" },
    { id: "dailyRate", label: "Daily Rate" },
    { id: "estimation", label: "Estimation" },
    { id: "budget", label: "Budget" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle checkbox changes
  const handleConfigChange = (id) => {
    const newConfig = { ...config, [id]: !config[id] };
    setConfig(newConfig);
    onConfigChange(newConfig);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="p-1.5 bg-white border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 shadow-sm" title="Edit Configuration">
        <IoSettingsSharp className="w-3.5 h-3.5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-30 w-64 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 max-h-96 overflow-y-auto">
          <div className="px-2.5 pb-1.5 mb-1 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500">Select fields to display</span>
          </div>

          <div className="grid grid-cols-1 gap-0.5 px-1.5">
            {configOptions.map((option) => (
              <label key={option.id} className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer text-sm transition-colors duration-150">
                <input
                  type="checkbox"
                  checked={!!config[option.id]}
                  onChange={() => handleConfigChange(option.id)}
                  className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-gray-700 text-xs">{option.label}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-between px-3 pt-1.5 mt-1 border-t border-gray-100">
            <button
              onClick={() => {
                const allSelected = configOptions.reduce((acc, opt) => ({ ...acc, [opt.id]: true }), {});
                setConfig(allSelected);
                onConfigChange(allSelected);
              }}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium">
              Select all
            </button>
            <button
              onClick={() => {
                const noneSelected = configOptions.reduce((acc, opt) => ({ ...acc, [opt.id]: false }), {});
                setConfig(noneSelected);
                onConfigChange(noneSelected);
              }}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium">
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
